/**
 * This file may contain code that uses generative AI for code assistance, unit testing and/or entire functions.
 * The generative model(s) used may be a combination of GitHub Copilot, OpenAI ChatGPT or others.
 */

import { useContext } from 'react';

import { CommandIntelligenceScreenContext, NOOP_COMMAND_INTELLIGENCE_ACTIONS } from '../context';
import { useCommandIntelligenceScreenActions } from './useCommandIntelligenceScreenActions';
import { useCommandIntelligenceScreenHost } from './useCommandIntelligenceScreenHost';

/**
 * Command Intelligence screen bridge hook.
 *
 * Host (call once per route tree, or use CommandIntelligenceBridge):
 *   const actions = useCommandIntelligenceScreen({ scopeId, commandIntelligenceConfig, ... });
 *
 * Consumer (descendants under the bridge):
 *   const { openList, openItem } = useCommandIntelligenceScreen();
 */
export const useCommandIntelligenceScreen = ({
  scopeId,
  commandIntelligenceConfig,
  renderContextBar,
  getItemKey,
  availableItems,
  enabled,
  maxAttachedItems,
  normalizeContextItem,
  normalizeContextItems,
  onListTruncated,
} = {}) => {
  const contextValue = useContext(CommandIntelligenceScreenContext);
  const isHost = Boolean(scopeId);

  const actions = useCommandIntelligenceScreenActions({
    maxAttachedItems,
    normalizeContextItem,
    normalizeContextItems,
    onListTruncated,
  });

  useCommandIntelligenceScreenHost({
    scopeId: isHost ? scopeId : undefined,
    commandIntelligenceConfig: isHost ? commandIntelligenceConfig : undefined,
    renderContextBar: isHost ? renderContextBar : undefined,
    getItemKey: isHost ? getItemKey : undefined,
    availableItems: isHost ? availableItems : [],
    enabled: isHost ? enabled : false,
    maxAttachedItems,
  });

  if (!isHost) {
    return contextValue ?? NOOP_COMMAND_INTELLIGENCE_ACTIONS;
  }

  return actions;
};
